// Owner Home — instrument panel (docs/design/console-personas.md §3.3).
// Fleet truth strip (banner + badge) lives in the shell and is never scoped here.

import { amVisibleDisplayText } from "../../../ingest-worker/src/am_visible_text.js";
import {
  AdminApiError,
  fleetQueueListCapFired,
  type AdminClient,
} from "../api.js";
import { MONTH_NOT_LOADED_TEXT, notPublishedReason } from "../console/data-source.js";
import type {
  ActivityItem,
  ActivityPage,
  AdminMonthsResponse,
  BalancesSnapshot,
  BookedBy,
  LedgerAccount,
  LedgerNotPublishedReason,
  LedgerStatement,
  PnlData,
} from "../console/types.js";
import { clear, h } from "../dom.js";
import {
  resolveScopeMembers,
  scopeLabel,
  type HomeScope,
} from "../scope.js";
import {
  type AdminHubIntakeCounts,
  type CompanyHealth,
  type CompanySlug,
  type DashboardGroup,
  type OnboardingStatus,
  type QueueItem,
  type RouteName,
  type StaffRole,
} from "../types.js";
import { bankLineStatusWord } from "../ui/activity-words.js";
import { renderCheckNow } from "../ui/check-now.js";
import { disclosure, svgEl, statTile } from "../ui/components.js";
import { mapWithConcurrency } from "../ui/concurrency.js";
import {
  countOrMoreText,
  countText,
  countedText,
  monthText,
  pluralText,
  timeAgoText,
} from "../ui/display-words.js";
import * as face from "../ui/face.js";
import type { CompanyDirectory } from "../ui/face.js";
import {
  actionableFor,
  bankCheckedAt,
  bankConnection,
  bankNeedsReconnect,
  cashFigure,
  monthAction,
  reviewCards,
  type BankConnection,
} from "../ui/facts.js";
import { failureCopy, outcomeSentence } from "../ui/failure.js";
import { navLabel, sentence } from "../ui/sentences.js";
import { runWrite } from "../ui/write.js";
import { adminErrorBodyMessage } from "../refusals.js";
import { createFreshnessCaption } from "../ui/freshness.js";
import {
  homeCountsLine,
  hubIntakeCountsTotal,
} from "../ui/hub-intake-words.js";
import {
  measuredClearHeadline,
  measuredSetOf,
  type MeasuredSet,
} from "../ui/measured-claim.js";
import {
  decimalToCents,
  formatAsOf,
  formatCents,
  sumUsdCentsExact,
  type UsdCents,
} from "../ui/money.js";
import { lastHealth, navigateTo, subscribeHealth } from "../ui/nav-bus.js";
import { setReviewScopeHint } from "../ui/review-scope.js";
import { defaultPeriod, periodNotAfterUtc, shiftPeriod } from "../ui/period-control.js";

/** Cap concurrent balances GETs under the 60 req/min/IP admin ceiling. */
export const HOME_BALANCES_CONCURRENCY = 4;

/**
 * One Activity read serves both "Recent activity" and the booked-this-month
 * sentence, so the sentence counts as much of the month as one read can hold.
 * 100 is the Worker's MAX_PAGE_SIZE (ingest-worker/src/activity.ts). Asking for
 * more than one page would cost requests; when the month is longer than this
 * the sentence says "At least" instead.
 */
export const ACTIVITY_PROOF_PAGE_SIZE = 100;

export interface HomePageOptions {
  client: AdminClient;
  root: HTMLElement;
  scope: HomeScope;
  groups: readonly DashboardGroup[];
  /** All active company slugs (stable order from companies list). */
  companySlugs: readonly CompanySlug[];
  companyLabels: ReadonlyMap<string, string>;
  /** Full display names for Home. Falls back to companyLabels. */
  companyNames?: ReadonlyMap<string, string>;
  /** The shell's one company directory (B2a-1). */
  companyDirectory?: CompanyDirectory;
  isCurrent?: () => boolean;
  /** Signed-in role; decides whose onboarding steps count as "waiting on you". */
  role: StaffRole;
  /** Open Accounts for a company (whole-row target). */
  openAccounts: (company: CompanySlug) => void;
  /** To review; the shell highlights its To review destination. */
  openReview: () => void;
  /** Open a company's onboarding checklist. */
  openChecklist: (company: CompanySlug) => void;
  /** Clock for the current month and the 24-hour data-flowing window. */
  now?: () => Date;
  /**
   * The shell's last fleet-health read failed (round 10, C-13). The previous
   * reading may still be shown, but only as the last one, never as current.
   */
  statusReadFailed?: () => boolean;
  /**
   * The shell's in-flight fleet-health read, so Home waits for the reading it
   * is about to speak for instead of painting the previous one (C-13).
   */
  healthReadSettled?: () => Promise<void>;
  /**
   * A message from an action that re-read this page (a 409 on Return to
   * account manager). It is painted after the re-read, so the sentence stays
   * on screen even when the row it was about is gone (round 11, R7-16).
   */
  needsYouNotice?: string | undefined;
}

type MemberBalance =
  | { status: "ok"; snapshot: BalancesSnapshot; cashCents: UsdCents | null }
  | { status: "error" }
  | { status: "empty"; snapshot: BalancesSnapshot };

/** The message an action left for the re-read page (round 11, R7-16). */
function needsYouNoticeLine(opts: HomePageOptions): HTMLElement | null {
  const text = opts.needsYouNotice?.trim() ?? "";
  if (text === "") return null;
  return h("p", {
    class: "banner banner-err",
    role: "status",
    "data-role": "needs-you-notice",
  }, [text]);
}

/**
 * Home instrument panel: group cash headline (no silent partial sums),
 * per-company rows with review_queue count only, awareness line.
 */
const DAY_MS = 24 * 60 * 60 * 1000;

/** The one company directory Home reads names from (B2a-1). */
function directoryFor(opts: HomePageOptions): CompanyDirectory {
  if (opts.companyDirectory !== undefined) return opts.companyDirectory;
  return face.createCompanyDirectory(
    opts.companySlugs.map((slug) => ({
      slug,
      displayName: opts.companyNames?.get(slug) ?? opts.companyLabels.get(slug) ?? null,
    })),
    { complete: true },
  );
}

/**
 * The Worker's already-resolved 409 is the ONLY 409 that means "someone
 * answered this card": every other conflict (the card changed, it is not
 * escalated) is one the person can retry. The predicate is private to
 * `queue.ts`, which this lane may not edit, so the Worker's own message is
 * matched here (round 2, B2a LOW; the same rule as dispatch-b §3.1 / E-B10).
 */
function isAlreadyResolvedConflict(error: unknown): boolean {
  if (!(error instanceof AdminApiError) || error.status !== 409) return false;
  return (
    (adminErrorBodyMessage(error.body) ?? "").trim().toLowerCase() ===
    "queue item is already resolved"
  );
}

const BOOKS_REFRESH_COPY =
  "The Books view refreshes after each booking run and about every hour; new bank and wallet activity is booked at the next booking run (up to 8 hours).";

/** Closing balances of accounts the ledger marked isCash. Null if any amount will not parse. */
export function sumIsCashClosingCents(accounts: readonly LedgerAccount[]): UsdCents | null {
  let total = 0n;
  let saw = false;
  for (const account of accounts) {
    if (!account.isCash) continue;
    const cents = decimalToCents(account.closing);
    if (cents === null) return null;
    total += cents;
    saw = true;
  }
  return saw ? total : 0n;
}

/**
 * The five G7 values, in the owner's reading order, with the singular and
 * plural wording the Activity page already uses (`BOOKED_BY_WORDS` in
 * transactions.ts, lane Y1's file).
 *
 * The person bucket is person-neutral: `by: "person"` means any console user
 * (an account manager approving a card books it as queue-approved-{am}), so
 * "by you" would claim the reader booked what someone else booked. Owner
 * decision 2026-10-27, verbatim: "By a person (Recommended)". One wording for
 * both roles.
 */
const BOOKED_BY_COUNT_WORDS: Readonly<
  Record<BookedBy, { one: string; many: string }>
> = {
  receipt: { one: "from a receipt", many: "from receipts" },
  rule: { one: "by a rule", many: "by rules" },
  ai: { one: "by AI", many: "by AI" },
  system_match: {
    one: "matched automatically",
    many: "matched automatically",
  },
  person: { one: "by a person", many: "by people" },
};

export interface BookedMonthSummary {
  /** Rows this month whose loaded books copy proves a booking. */
  total: number;
  /** How many of those carry each G7 value. */
  by: Record<BookedBy, number>;
  /** Booked rows whose ledger copy proves no value. */
  noReason: number;
  /** Rows this month waiting on this person. */
  needsYou: number;
  /** False when Activity held more rows than this one read asked for. */
  complete: boolean;
}

/**
 * Sum the month's Activity rows into the proof sentence's numbers. A row counts
 * as booked only when the loaded books copy proves a booking on it (`booking`),
 * the same rule that makes a row say "Booked" (ui/activity-words.ts).
 */
export function bookedMonthSummary(page: ActivityPage): BookedMonthSummary {
  const by: Record<BookedBy, number> = {
    receipt: 0,
    rule: 0,
    ai: 0,
    system_match: 0,
    person: 0,
  };
  let total = 0;
  let noReason = 0;
  let needsYou = 0;
  for (const item of page.items) {
    if (item.state === "needs_you") needsYou += 1;
    if (item.booking === undefined) continue;
    total += 1;
    if (item.booking.by === undefined) noReason += 1;
    else by[item.booking.by] += 1;
  }
  return { total, by, noReason, needsYou, complete: !page.hasMore };
}

/**
 * One plain sentence: what booked itself this month, by what, and how much
 * still needs this person. A capped read never claims the whole month. The
 * sentence is the same for both roles: no bucket says "you".
 */
export function bookedMonthSentence(summary: BookedMonthSummary): string {
  const atLeast = summary.complete ? "" : "At least ";
  const parts: string[] = [];
  const words = BOOKED_BY_COUNT_WORDS;
  for (const key of [
    "receipt",
    "rule",
    "ai",
    "system_match",
    "person",
  ] as const) {
    const count = summary.by[key];
    if (count === 0) continue;
    parts.push(pluralText(count, words[key].one, words[key].many));
  }
  if (summary.noReason > 0) {
    parts.push(
      pluralText(summary.noReason, "with no reason shown", "with no reason shown"),
    );
  }
  const needs =
    summary.needsYou === 0
      ? ""
      : ` ${atLeast}${pluralText(summary.needsYou, "needs", "need")} you.`;
  if (summary.total === 0) {
    const nothing = summary.complete
      ? "Nothing has booked yet this month."
      : "Nothing has booked yet in what we have loaded this month.";
    return `${nothing}${needs}`.trim();
  }
  return `${atLeast}${String(summary.total)} booked this month: ${parts.join(", ")}.${needs}`;
}

/**
 * "September 2026: Closed · October 2026: Needs you" — this month and the one
 * before it, from the Months read. Null when the read failed or neither month
 * is in it: Home then says nothing rather than guessing. The word is the one
 * shared fact (`monthAction`, B2a-3), never a second derivation.
 *
 * `undated` is how many blockers are not tied to any month. They are named
 * under decision 9's heading, so the line never claims a month is the whole
 * story while cards outside the window exist.
 */
export function monthStatusLine(
  months: AdminMonthsResponse | null,
  now: Date,
  role: StaffRole,
): string | null {
  if (months === null) return null;
  // Decision 9 counts every card the Worker put outside the window, for both
  // readers (round 2, M2): an owner-held card is still a card outside the
  // months shown, so the account manager is not told the month is clear while
  // Months names it. Months shows the same heading for the same company.
  const undated = months.undatedBlockers.length;
  const current = defaultPeriod(now);
  const previous = shiftPeriod(current, -1);
  const wanted = [previous, current];
  const parts: string[] = [];
  for (const period of wanted) {
    const month = months.months.find((candidate) => candidate.period === period);
    if (month === undefined) continue;
    const label = monthText(period);
    if (label === "") continue;
    const word = monthAction(month, role).word;
    // Decision 9: a month may not say "Nothing waiting" while cards outside
    // the months shown exist; the outside line below is the whole truth then.
    if (undated > 0 && word === "Nothing waiting") continue;
    parts.push(`${label}: ${word}`);
  }
  if (undated > 0) {
    parts.push(`${sentence("outside_months_shown", role)}: ${String(undated)}`);
  }
  if (parts.length === 0) return null;
  return parts.join(" · ");
}

/**
 * True when Home's month line names open statement or bank-line work. B2a-4:
 * "All caught up." is about the cards, so it may not claim the whole page is
 * clear while the month beside it is not.
 */
function monthLineShowsOpenMoneyWork(months: AdminMonthsResponse | null): boolean {
  if (months === null) return false;
  const moneyWork = new Set([
    "statements_not_matching",
    "statements_to_confirm",
    "statements_saving",
    "bank_lines_waiting",
    "bank_lines_failed",
  ]);
  return (
    months.undatedBlockers.some((blocker) => moneyWork.has(blocker.key)) ||
    months.months.some((month) =>
      month.blockers.some((blocker) => moneyWork.has(blocker.key)),
    )
  );
}

function ageMs(iso: string, now: Date): number | null {
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return null;
  return now.getTime() - t;
}

function isOlderThanDay(iso: string, now: Date): boolean {
  const age = ageMs(iso, now);
  return age === null || age >= DAY_MS;
}

/**
 * The shared sentences of the round-10 fix wave
 * (docs/design/rd-z-fixes-background.md §3). Z4 and Z5 must say the same thing
 * about the same state, so these two are written once, here, and the words are
 * the table's words.
 */
export function bookingFailedCopy(role: StaffRole): string {
  // Decision 3 (resolution E5): one home, `ui/sentences.ts`. The helper stays
  // exported because health.ts, ui/check-now.ts and queue.ts still call it.
  return sentence("booking_failed", role);
}

/**
 * The books could not be built for the month. The month is named in full
 * ("October 2026"); when the period cannot be read the sentence says "this
 * month" rather than inventing a name.
 */
function booksNotBuiltSentence(period: string, role: StaffRole): string {
  const month = monthText(period);
  const when = month === "" ? "this month" : month;
  return sentence("books_not_built", role, { month: when });
}

/**
 * Why a tile has no figure. Short on purpose: the one sentence that says what
 * happened and what to do lives once, on the tile that carries the failed
 * figure (`booksNotBuiltSentence`), so the same long sentence is never printed
 * three times on one screen.
 */
export function statementUnavailableCopy(reason: LedgerNotPublishedReason): string {
  if (reason === "ledger_not_published") return MONTH_NOT_LOADED_TEXT;
  if (reason === "ledger_view_not_enabled") {
    return "Books view is not switched on for this company.";
  }
  // Role rule (rd polish rules §1): the owner is the person reading, so the
  // owner is never told to tell the owner. Books and Reports say the same.
  // The role-aware next step is in booksNotBuiltSentence; this label is one for
  // both roles, so the screen never says the long sentence twice.
  return "Could not be built.";
}

/** Latest processing run for one company, any status. */
export function latestProcessingRun(
  runs: CompanyHealth["lastRuns"],
): CompanyHealth["lastRuns"][number] | null {
  let best: CompanyHealth["lastRuns"][number] | null = null;
  for (const run of runs) {
    if (run.runType !== "processing") continue;
    if (best === null || run.startedAt > best.startedAt) best = run;
  }
  return best;
}

export function bookingFlowCopy(
  runs: CompanyHealth["lastRuns"],
  now: Date,
  role: StaffRole,
): string {
  const flow = bookingFlowOf(runs, now);
  switch (flow.kind) {
    case "none":
      return "Booking has not run recently";
    case "running":
      return "Booking is running now";
    case "failed":
      // Round 10 (C-12): the owner is the end of the line, so the sentence
      // says what to do next as well as what happened. The account manager is
      // sent to the owner, once (rd-z-fixes-background §3).
      return bookingFailedCopy(role);
    case "ran": {
      const when = timeAgoText(flow.at, now);
      return `Booking last ran ${when === "" ? "an unknown time ago" : when}`;
    }
    case "stale":
      return "Booking has not run recently";
  }
}

/** The one reading of "where booking is" that the tile, the band and the
 * switch row all share, so none of them can disagree about the last run. */
type BookingFlow =
  | { kind: "none" }
  | { kind: "running" }
  | { kind: "failed"; at: string }
  | { kind: "ran"; at: string }
  | { kind: "stale"; at: string };

function bookingFlowOf(
  runs: CompanyHealth["lastRuns"],
  now: Date,
): BookingFlow {
  const latest = latestProcessingRun(runs);
  if (latest === null) return { kind: "none" };
  if (latest.status === "running") return { kind: "running" };
  if (latest.status === "failed") return { kind: "failed", at: latest.startedAt };
  if (!isOlderThanDay(latest.startedAt, now)) {
    return { kind: "ran", at: latest.startedAt };
  }
  return { kind: "stale", at: latest.startedAt };
}

/**
 * When the bank was last checked: the item's own reading (the one home in
 * `ui/facts.ts`), else the last bank-sync run. A page never reads `plaidItems`.
 */
function latestBankCheck(company: CompanyHealth): string | null {
  const own = bankCheckedAt(company);
  if (own !== null) return own;
  let latest: string | null = null;
  for (const run of company.lastRuns) {
    if (run.runType !== "plaid_sync") continue;
    const at = run.completedAt ?? run.startedAt;
    if (latest === null || at > latest) latest = at;
  }
  return latest;
}

export function bankFlowCopy(
  company: CompanyHealth | null,
  now: Date,
  // Decision 10: an unknown reader is the account manager (least access).
  role: StaffRole = "account_manager",
): {
  text: string;
  needsReconnect: boolean;
} {
  if (company === null) {
    return { text: "Bank status is not available yet.", needsReconnect: false };
  }
  if (bankNeedsReconnect(company)) {
    return { text: sentence("bank_needs_reconnect", role), needsReconnect: true };
  }
  const state = bankConnection(company);
  if (state === "none") {
    return { text: sentence("no_bank_connected", role), needsReconnect: false };
  }
  if (state === "never_read") {
    return { text: "Connected.", needsReconnect: false };
  }
  const latest = latestBankCheck(company);
  if (latest === null) return { text: "Connected.", needsReconnect: false };
  const when = timeAgoText(latest, now);
  return {
    text: when === "" ? "Connected." : `Connected, last checked ${when}`,
    needsReconnect: false,
  };
}

export type HomeStatusTone = "ok" | "warn" | "crit" | "info";

export interface HomeStatus {
  text: string;
  tone: HomeStatusTone;
}

/**
 * The one reading of "what does the last health read prove", in the words the
 * stale sentence needs ("Last known: connected, checked 7 minutes ago.").
 * Never the all-clear.
 */
function lastKnownBankText(company: CompanyHealth, now: Date): string {
  if (bankNeedsReconnect(company)) return "needs reconnecting";
  const state = bankConnection(company);
  if (state === "none") return "no bank connected";
  const latest = latestBankCheck(company);
  if (latest === null) return "connected";
  const when = timeAgoText(latest, now);
  return when === "" ? "connected" : `connected, checked ${when}`;
}

/**
 * Round 2 (6): the booking line is drawn from the same reading, so a failed
 * read makes it stale in the same words — the previous run is the LAST KNOWN
 * one, not "booking last ran …" as if it were current.
 */
function lastKnownBookingText(runs: CompanyHealth["lastRuns"], now: Date): string {
  const flow = bookingFlowOf(runs, now);
  switch (flow.kind) {
    case "none":
      return "booking has not run recently";
    case "running":
      return "booking is running";
    case "failed":
      return "the last booking run failed";
    case "ran": {
      const when = timeAgoText(flow.at, now);
      return `booking last ran ${when === "" ? "an unknown time ago" : when}`;
    }
    case "stale":
      return "booking has not run recently";
  }
}

/** The shared stale form for the booking line (rd-z-fixes-background §3). */
function staleBookingCopy(runs: CompanyHealth["lastRuns"], now: Date): string {
  return `We could not check just now. Last known: ${lastKnownBookingText(runs, now)}.`;
}

/**
 * One plain sentence answering "am I OK?" from the shell's health read and
 * this page's ledger read. It never claims all-clear from a missing read.
 *
 * `statusStale` is the shell saying its last health read FAILED (round 10,
 * C-13). The previous reading is still worth showing, but only as the last
 * one: "We could not check just now. Last known: connected, checked 7 minutes
 * ago." The all-clear sentence comes back only after a successful read.
 * `monthUnbuilt` is either of this page's two book reads saying the month's
 * figures could not be built (round 2, 3), so the band never says "everything
 * is up to date" beside a month tile that could not be built.
 */
export function homeStatus(
  health: CompanyHealth | null,
  ledger: LedgerAccountsLoad,
  now: Date,
  options: { stale?: boolean; monthUnbuilt?: boolean; role?: StaffRole } = {},
): HomeStatus {
  const role = options.role ?? "account_manager";
  const statusStale = options.stale === true;
  if (health === null) {
    return { text: "We could not check just now.", tone: "info" };
  }
  if (statusStale) {
    return {
      text: `We could not check just now. Last known: ${lastKnownBankText(health, now)}.`,
      tone: "info",
    };
  }
  const bank = bankFlowCopy(health, now, role);
  if (bank.needsReconnect) {
    return { text: "A bank connection needs reconnecting.", tone: "warn" };
  }
  const booking = bookingFlowOf(health.lastRuns, now);
  if (booking.kind === "failed") {
    // The band says what happened; the action lives once on the flow tile.
    return { text: sentence("booking_failed_short", role), tone: "crit" };
  }
  // The all-clear sentence below says "connected", so it needs proof of a bank
  // (`bankConnection`) and a check inside the same one-day window the booking
  // run and the books are held to.
  const connection = bankConnection(health);
  if (connection === "none") {
    return { text: sentence("no_bank_connected", role), tone: "info" };
  }
  if (connection === "never_read") {
    return { text: "We cannot tell when the bank was last checked.", tone: "info" };
  }
  const checkedAt = latestBankCheck(health);
  if (checkedAt === null) {
    return { text: "We cannot tell when the bank was last checked.", tone: "info" };
  }
  if (isOlderThanDay(checkedAt, now)) {
    return {
      text: `The bank was last checked ${timeAgoText(checkedAt, now) || "a while ago"}.`,
      tone: "warn",
    };
  }
  if (booking.kind === "running") {
    return { text: "A booking run is in progress.", tone: "info" };
  }
  if (booking.kind === "none" || booking.kind === "stale") {
    return { text: "The last booking run was more than a day ago.", tone: "warn" };
  }
  if (!ledger.ok) {
    if (ledger.reason === "ledger_not_published") {
      return { text: "The books have not been loaded yet.", tone: "info" };
    }
    if (ledger.reason === "ledger_view_not_enabled") {
      return { text: sentence("books_view_off", role), tone: "info" };
    }
    if (ledger.reason === "statements_unavailable") {
      // The month itself could not be built: the shared sentence with the next
      // step is on the data-flowing tile, so the band states it once, short.
      return { text: "The books could not be built.", tone: "warn" };
    }
    return { text: "The books could not be checked.", tone: "warn" };
  }
  if (isOlderThanDay(ledger.asOf, now)) {
    return { text: "The books were last updated more than a day ago.", tone: "warn" };
  }
  if (options.monthUnbuilt === true) {
    // Round 2 (3): the month's own figures could not be built (the statement
    // read failed) even though the accounts read is fine. The tile that
    // carries the figure says what to do; this band must not claim everything
    // is up to date beside it.
    return { text: "The books could not be built.", tone: "warn" };
  }
  return { text: "Bank, booking and books are all current.", tone: "ok" };
}

type ActivityLoad =
  | { ok: true; page: ActivityPage }
  | { ok: false };

/**
 * The shell fetches fleet health after navigation. Home should not claim "we
 * cannot check" just because that read has not landed yet, so wait briefly for
 * the in-memory nav-bus result (no extra network read).
 */
async function healthForCompany(
  slug: CompanySlug,
  stillCurrent: () => boolean,
): Promise<CompanyHealth | null> {
  for (let attempt = 0; attempt < 20; attempt += 1) {
    const health = lastHealth()?.companies.find((company) => company.company === slug);
    if (health !== undefined) return health;
    if (!stillCurrent()) return null;
    await new Promise((resolve) => setTimeout(resolve, 50));
  }
  return null;
}

/**
 * Wait for the shell's in-flight fleet-health read, so Home's status sentence
 * speaks for a reading that has actually finished (C-13). Bounded at two
 * seconds: a hung read must not hold the page.
 *
 * Returns false when the read had NOT settled by the deadline (round 2, 6):
 * the caller then repaints when it lands, because the reading on screen is
 * about to become either current or stale.
 */
export const HEALTH_SETTLE_WAIT_MS = 2_000;

async function settleHealthRead(opts: HomePageOptions): Promise<boolean> {
  const settled = opts.healthReadSettled?.();
  if (settled === undefined) return true;
  let done = false;
  await Promise.race([
    settled.then(() => {
      done = true;
    }),
    new Promise<void>((resolve) => setTimeout(resolve, HEALTH_SETTLE_WAIT_MS)),
  ]);
  return done;
}

async function loadActivity(
  client: AdminClient,
  slug: CompanySlug,
  period: string,
  stillCurrent: () => boolean,
): Promise<ActivityLoad> {
  try {
    const page = await client.listActivity(slug, {
      period,
      page: 1,
      pageSize: ACTIVITY_PROOF_PAGE_SIZE,
    });
    if (!stillCurrent()) return { ok: false };
    return { ok: true, page };
  } catch {
    return { ok: false };
  }
}

type MonthsLoad =
  | { ok: true; months: AdminMonthsResponse }
  | { ok: false };

/**
 * The Months read Home needs for "where the month stands". One read; a failure
 * hides the line rather than guessing a status.
 */
async function loadMonths(
  client: AdminClient,
  slug: CompanySlug,
  stillCurrent: () => boolean,
): Promise<MonthsLoad> {
  try {
    const months = await client.getMonths(slug);
    if (!stillCurrent()) return { ok: false };
    return { ok: true, months };
  } catch {
    return { ok: false };
  }
}

interface HubCounts {
  counts: AdminHubIntakeCounts;
  /** False when a scan limit was hit: the counts may be short. */
  countsComplete: boolean;
}

async function loadHubCounts(
  client: AdminClient,
  slug: CompanySlug,
  period: string,
  stillCurrent: () => boolean,
): Promise<HubCounts | null> {
  try {
    // The same month the Receipts and bills page opens on (periodNotAfterUtc), never the
    // Worker's UTC default: in a US evening at month end that is already next
    // month, and the line would count items the page it opens does not show.
    const response = await client.listHubIntake(slug, { period, limit: 0 });
    if (!stillCurrent()) return null;
    // A company the hub cannot send receipts or bills to can still hold
    // transfers (WV2 lists them), so the line shows whenever something came
    // in and stays silent only when nothing did.
    if (
      response.hubSetup === "not_set_up" &&
      hubIntakeCountsTotal(response.counts) === 0
    ) {
      return null;
    }
    return {
      counts: response.counts,
      countsComplete: response.countsComplete,
    };
  } catch {
    return null;
  }
}

function paintHubCountsLine(hub: HubCounts, role: StaffRole): HTMLElement {
  const line = h(
    "button",
    {
      type: "button",
      class: "home-hub-counts",
      "data-role": "home-hub-counts",
      "data-action": "home-hub-intake",
    },
    [homeCountsLine(hub.counts, role, hub.countsComplete)],
  );
  line.addEventListener("click", () => {
    navigateTo("receipt-shelf");
  });
  const host = h("div", { class: "home-hub-block", "data-role": "home-hub-block" }, [line]);
  // "5 waiting" is four states added together (waiting for the bank line, no
  // bank line, not booked yet, with the practice), so the line says what it
  // means instead of naming one of them. The counts line itself lives in
  // ui/hub-intake-words.ts, which this lane does not own: see the PR.
  const waiting =
    hub.counts.waiting_for_the_bank_line +
    hub.counts.on_the_shelf +
    hub.counts.not_booked_yet +
    hub.counts.with_the_practice;
  if (waiting > 0) {
    host.append(
      h("p", { class: "home-hub-waiting muted", "data-role": "home-hub-waiting" }, [
        "Waiting means it has not reached the books yet.",
      ]),
    );
  }
  return host;
}

/** A value that reads as an internal key: no capitals, no spaces, no "@". */
const PERSON_KEY_TEXT = /^[a-z0-9]+(?:[-_.][a-z0-9]+)*$/;

/**
 * Who escalated a card, in words a person reads. The API's `escalatedBy` is
 * usually an internal key ("demo-am"), which is not a name (6(c)); a real name
 * or an address is shown as it came. The value is already hazard-stripped by
 * the caller.
 */
export function escalatedByText(value: string): string {
  const cleaned = value.trim();
  if (cleaned === "") return "an account manager";
  if (PERSON_KEY_TEXT.test(cleaned)) return "an account manager";
  return cleaned;
}

function activityDate(iso: string): string {
  const date = new Date(`${iso}T12:00:00Z`);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  });
}

/** Newest first; a row without a usable date keeps the order the server sent. */
function newestFirst(items: readonly ActivityItem[]): ActivityItem[] {
  return [...items].sort((a, b) => {
    const left = Date.parse(a.date);
    const right = Date.parse(b.date);
    if (Number.isNaN(left) || Number.isNaN(right)) return 0;
    return right - left;
  });
}

/**
 * The proof sentence: what booked itself this month, from the same Activity
 * read the list below uses. Links to Activity for the month.
 */
function paintBookedProof(
  load: ActivityLoad,
  period: string,
): HTMLElement | null {
  if (!load.ok) return null;
  const section = h("section", {
    class: "home-proof",
    "data-role": "home-booked-proof",
  });
  section.append(
    h("p", { class: "home-proof-sentence", "data-role": "home-booked-sentence" }, [
      bookedMonthSentence(bookedMonthSummary(load.page)),
    ]),
  );
  const open = h(
    "button",
    { type: "button", class: "btn btn-ghost", "data-action": "home-booked-activity" },
    ["See this month in Activity"],
  );
  open.addEventListener("click", () => {
    navigateTo("transactions", { period });
  });
  section.append(open);
  return section;
}

/**
 * "September 2026: closed · October 2026: 2 things left" plus the way to the
 * Months page. Absent when the Months read failed: silence, never a guess.
 */
function paintMonthStatus(
  load: MonthsLoad,
  now: Date,
  role: StaffRole,
): HTMLElement | null {
  if (!load.ok) return null;
  const line = monthStatusLine(load.months, now, role);
  if (line === null) return null;
  const open = h(
    "button",
    { type: "button", class: "btn btn-ghost", "data-action": "home-months" },
    ["Months"],
  );
  open.addEventListener("click", () => {
    navigateTo("months");
  });
  return h("p", { class: "home-month-status", "data-role": "home-month-status" }, [
    document.createTextNode(`${line} · `),
    open,
  ]);
}

function paintActivity(load: ActivityLoad, onRetry: () => void): HTMLElement {
  const section = h("section", {
    class: "home-activity",
    "data-role": "home-activity",
  }, [h("h2", { class: "home-activity-heading" }, ["Recent activity"])]);

  if (!load.ok) {
    const retry = h(
      "button",
      { type: "button", class: "btn btn-ghost", "data-action": "retry-activity" },
      ["Try again"],
    );
    retry.addEventListener("click", () => {
      onRetry();
    });
    section.append(
      h("p", { class: "muted", "data-role": "home-activity-empty" }, [
        "Activity could not be loaded. ",
        retry,
      ]),
    );
    return section;
  }

  if (load.page.items.length === 0) {
    section.append(
      h("p", { class: "muted", "data-role": "home-activity-empty" }, [
        "No activity yet this month.",
      ]),
    );
  } else {
    const list = h("ul", { class: "home-activity-list", "data-role": "home-activity-list" });
    for (const item of newestFirst(load.page.items).slice(0, 5)) {
      const cents = decimalToCents(item.amount);
      const amount = cents === null ? item.amount : formatCents(cents);
      list.append(
        h("li", { class: "home-activity-row", "data-role": "home-activity-row" }, [
          h("span", { class: "home-activity-date" }, [activityDate(item.date)]),
          h("span", { class: "home-activity-what" }, [
            amVisibleDisplayText(item.description),
          ]),
          h("span", { class: "home-activity-amount" }, [amount]),
          h("span", { class: "home-activity-state" }, [bankLineStatusWord(item)]),
        ]),
      );
    }
    section.append(list);
  }

  const all = h(
    "button",
    { type: "button", class: "btn btn-ghost", "data-action": "home-activity-all" },
    ["See all activity"],
  );
  all.addEventListener("click", () => {
    navigateTo("transactions");
  });
  section.append(all);
  return section;
}

function paintHomeStatus(status: HomeStatus): HTMLElement {
  const link = h(
    "button",
    { type: "button", class: "btn btn-ghost", "data-action": "home-status-link" },
    [navLabel("health")],
  );
  link.addEventListener("click", () => {
    navigateTo("health");
  });
  return h(
    "section",
    {
      class: `home-status home-status-${status.tone}`,
      "data-role": "home-status",
      "data-state": status.tone,
    },
    [
      h("p", { class: "home-status-text", "data-role": "home-status-text" }, [status.text]),
      link,
    ],
  );
}

function asPnl(data: unknown): PnlData | null {
  if (typeof data !== "object" || data === null) return null;
  if (!("sections" in data) || !("netIncome" in data) || !("currency" in data)) return null;
  if (!Array.isArray(data.sections) || typeof data.netIncome !== "string") return null;
  if (typeof data.currency !== "string") return null;
  return data as PnlData;
}

function sectionTotal(data: PnlData, key: string): UsdCents | null {
  const section = data.sections.find((row) => row.key === key);
  if (section === undefined) return 0n;
  return decimalToCents(section.total);
}

function skeletonTile(tile: string): HTMLElement {
  return h(
    "div",
    { class: "stat-tile home-skeleton", "data-role": "home-skeleton", "data-tile": tile },
    [h("p", { class: "stat-label" }, ["Loading…"])],
  );
}

function tileRetry(label: string, onRetry: () => void): HTMLElement {
  const retry = h(
    "button",
    {
      type: "button",
      class: "btn btn-ghost",
      "data-action": "retry-tile",
      "aria-label": `Try again: ${label}`,
    },
    ["Try again"],
  );
  retry.addEventListener("click", () => {
    onRetry();
  });
  return h("div", { class: "home-tile-retry" }, [
    h("p", { class: "stat-label" }, [label]),
    h("p", {}, ["Could not load this."]),
    retry,
  ]);
}

function twoBarChart(income: UsdCents, moneyOut: UsdCents): { svg: SVGElement; text: string } {
  const incomeAbs = income < 0n ? -income : income;
  const outAbs = moneyOut < 0n ? -moneyOut : moneyOut;
  const max = incomeAbs > outAbs ? incomeAbs : outAbs;
  const barPx = (cents: UsdCents): number => {
    if (max <= 0n) return 2;
    const px = Number((cents * 48n) / max);
    if (!Number.isFinite(px)) return 2;
    return Math.max(2, Math.min(48, Math.round(px)));
  };
  const incomeH = barPx(incomeAbs);
  const outH = barPx(outAbs);
  const text = `Income ${formatCents(income)}. Money out ${formatCents(moneyOut)}.`;
  const title = svgEl("title");
  title.textContent = text;
  const incomeLabel = svgEl("text", { x: "8", y: "70", "font-size": "9" });
  incomeLabel.textContent = "Income";
  const outLabel = svgEl("text", { x: "64", y: "70", "font-size": "9" });
  outLabel.textContent = "Money out";
  const svg = svgEl(
    "svg",
    {
      viewBox: "0 0 120 74",
      width: "160",
      height: "96",
      role: "img",
      "aria-label": text,
      class: "home-bars",
    },
    [
      title,
      svgEl("rect", {
        x: "12",
        y: String(58 - incomeH),
        width: "36",
        height: String(incomeH),
        rx: "3",
        fill: "var(--navy)",
      }),
      svgEl("rect", {
        x: "68",
        y: String(58 - outH),
        width: "36",
        height: String(outH),
        rx: "3",
        fill: "var(--gold)",
      }),
      incomeLabel,
      outLabel,
    ],
  );
  return { svg, text };
}

function reasonOf(error: unknown): LedgerNotPublishedReason | null {
  return notPublishedReason(error);
}

/**
 * Single-company Home. Skeletons paint before any page read. Each tile
 * fills on its own; one failed read does not replace the page with an error.
 */
async function renderCompanyHome(opts: HomePageOptions): Promise<void> {
  const { client, root } = opts;
  const slug = opts.scope.kind === "company" ? opts.scope.slug : "";
  const now = (): Date => opts.now?.() ?? new Date();
  const stillCurrent = (): boolean => opts.isCurrent?.() ?? true;
  clear(root);

  const directory = directoryFor(opts);
  const labelFor = (company: CompanySlug): string =>
    face.company(company, directory, "sentence");
  root.append(
    h("h1", { class: "home-headline", "data-role": "home-headline" }, [
      face.company(slug, directory, "heading"),
    ]),
  );
  const actionNotice = needsYouNoticeLine(opts);
  if (actionNotice !== null) root.append(actionNotice);
  const asOfLine = h("p", {
    class: "home-as-of muted",
    "data-role": "home-as-of",
    hidden: "true",
  });
  root.append(asOfLine);

  const freshnessHost = h("div", { "data-role": "freshness-host" });
  root.append(freshnessHost);

  const statusHost = h("p", {
    class: "muted home-status-loading",
    "data-role": "home-status-loading",
  }, ["Checking your connections…"]);
  root.append(statusHost);

  const proofHost = h("div", { "data-role": "home-proof-host" });
  root.append(proofHost);
  const monthStatusHost = h("div", { "data-role": "home-month-status-host" });
  root.append(monthStatusHost);

  const grid = h("div", { class: "home-tiles", "data-role": "home-tiles" });
  const bankHost = skeletonTile("bank");
  const booksHost = skeletonTile("books");
  const reviewHost = skeletonTile("review");
  const monthHost = skeletonTile("month");
  monthHost.classList.add("home-tile-wide");
  grid.append(bankHost, booksHost, monthHost, reviewHost);
  root.append(grid);

  const activityHost = h("div", { "data-role": "home-activity-host" });
  root.append(activityHost);

  const flowHost = skeletonTile("flow");
  flowHost.classList.add("home-tile-wide");
  root.append(flowHost);

  const period = defaultPeriod(now());
  const next = h("nav", { class: "home-next", "data-role": "home-next", "aria-label": "Next steps" });
  const questions = h(
    "button",
    { type: "button", class: "btn btn-ghost", "data-action": "home-questions" },
    ["Answer your questions"],
  );
  questions.addEventListener("click", () => {
    openReviewHere();
  });
  const booksLink = h(
    "button",
    { type: "button", class: "btn btn-ghost", "data-action": "home-books" },
    ["See this month's books"],
  );
  booksLink.addEventListener("click", () => {
    navigateTo("general-ledger", { period });
  });
  const reportsLink = h(
    "button",
    { type: "button", class: "btn btn-ghost", "data-action": "home-reports" },
    ["See reports"],
  );
  reportsLink.addEventListener("click", () => {
    navigateTo("reports", { period });
  });
  next.append(questions, booksLink, reportsLink);
  root.append(next);

  const needsYou = h("section", {
    class: "needs-you",
    "data-role": "needs-you",
    hidden: "true",
  });
  root.append(needsYou);
  const todayOnboarding = h("section", {
    class: "today-onboarding",
    "data-role": "today-onboarding",
    hidden: "true",
  });
  root.append(todayOnboarding);
  const awareness = h("p", { class: "home-awareness", "data-role": "home-awareness" });
  root.append(awareness);

  const members = resolveScopeMembers(opts.scope, opts.groups, opts.companySlugs);
  void paintTodayOnboarding(
    todayOnboarding,
    members,
    opts.role,
    labelFor,
    client,
    opts.openChecklist,
    stillCurrent,
  ).catch(() => undefined);

  const retry = (): void => {
    // A retry re-reads the page; it is not the action that left a notice, so
    // the previous notice is dropped rather than repeated (round 2,
    // MUST-FIX 1).
    void renderHomePage({ ...opts, needsYouNotice: undefined });
  };

  // G11 hub counts: one counts-only read, started beside SH's reads. It is
  // advisory garnish: a slow or failed read never delays or fails Home, and
  // no line is painted when the hub is not set up or the read failed.
  const hubCounts = loadHubCounts(client, slug, periodNotAfterUtc(now()), stillCurrent);

  const [fleetSet, balances, ledgerAccounts, statement, activity, months] = await Promise.all([
    loadHomeQueue(client, stillCurrent),
    loadBalances(client, slug, stillCurrent),
    loadLedgerAccounts(client, slug, period, stillCurrent),
    loadStatement(client, slug, period, stillCurrent),
    loadActivity(client, slug, period, stillCurrent),
    loadMonths(client, slug, stillCurrent),
  ]);
  if (!stillCurrent()) return;
  // The read is the whole fleet's pending list, the same one the nav badge
  // uses (one cached request, not one per company). This company's cards are
  // a slice of it; the footer and the tile need the whole, or "in all" is
  // unreachable and the footer tells each company that every question waiting
  // is its own (integration review N1).
  //
  // The Worker returns only the OLDEST 500 pending across all companies, so
  // once the fleet reaches that cap this company's slice of it can be empty
  // while the company has cards waiting (integration re-check R1: the tile read
  // "0" for a company with 20). In that case, and only then, read this
  // company's own list; the fleet list still gives "in all" and the footer.
  const queueSet = await loadCompanyQueue(client, slug, fleetSet, stillCurrent);
  if (!stillCurrent()) return;

  // C-13: speak for the reading the shell has just taken, not the one before
  // it. Bounded, because a hung status read must not hold the page.
  const settledInTime = await settleHealthRead(opts);
  if (!stillCurrent()) return;
  const health =
    (await healthForCompany(slug, stillCurrent)) ??
    lastHealth()?.companies.find((company) => company.company === slug) ??
    null;
  // The health wait can take a second. A Home the user has already left must
  // not paint, or start the flow tile's check-now read for the old company.
  if (!stillCurrent()) return;
  const statusReadFailed = (): boolean => opts.statusReadFailed?.() ?? false;
  // Round 2 (3): the month is unbuilt if EITHER of this page's book reads says
  // so, and the band must not claim all-clear beside a month that could not be
  // built.
  const unbuiltMonth = monthUnbuilt(ledgerAccounts, statement);
  let statusElement = paintHomeStatus(
    homeStatus(health, ledgerAccounts, now(), {
      stale: statusReadFailed(),
      monthUnbuilt: unbuiltMonth,
      role: opts.role,
    }),
  );
  statusHost.replaceWith(statusElement);
  // "Updated 0m ago" and "The books were last updated more than a day ago."
  // are both true and read as a contradiction. The caption speaks for the
  // books, so when they are stale it carries their own time.
  const booksAsOf = ledgerAccounts.ok ? ledgerAccounts.asOf : null;
  const booksStale = booksAsOf !== null && isOlderThanDay(booksAsOf, now());
  freshnessHost.append(
    createFreshnessCaption({
      updatedAt: booksStale ? new Date(booksAsOf) : now(),
      onRefresh: () => {
        // Refresh drops a notice left by an action; it does not repeat it
        // (round 2, MUST-FIX 1).
        void renderHomePage({ ...opts, needsYouNotice: undefined });
      },
    }),
  );
  void hubCounts.then((hub) => {
    if (hub === null || !stillCurrent()) return;
    statusElement.insertAdjacentElement(
      "afterend",
      paintHubCountsLine(hub, opts.role),
    );
  });

  const proof = paintBookedProof(activity, period);
  if (proof !== null) proofHost.append(proof);
  const monthStatus = paintMonthStatus(months, now(), opts.role);
  if (monthStatus !== null) monthStatusHost.append(monthStatus);

  // Escalated cards are the owner's: an account manager cannot open them from
  // To review and the return action is owner-only on the Worker, so listing
  // them here sends the AM to a dead end (integration re-check M-B). The tile
  // and the footer already leave them out for an account manager.
  // On a capped fleet list To review cannot show this company's cards from the
  // list it shares with every other company, so the company is handed over and
  // To review reads that company's own list (re-check E5). On every list the
  // tile, this button and "Answer your questions" count ONE company, so the
  // hand-off always carries it (round 10, C-4): the person lands on the cards
  // the number they pressed names, not the fleet's.
  const openReviewHere = (): void => {
    setReviewScopeHint({ company: slug });
    opts.openReview();
  };
  paintNeedsYou(needsYou, opts.role === "owner" ? queueSet : null, labelFor, client, opts.role, async (notice) => {
    // The notice belongs to this one re-read: the next render starts clean
    // (round 2, MUST-FIX 1).
    await renderHomePage({ ...opts, needsYouNotice: notice });
  }, openReviewHere, stillCurrent);
  // The footer counts what this person can open, on the same rule as the tile:
  // an account manager's "in all" never includes the owner's escalated cards.
  paintAwareness(
    awareness,
    fleetSet === null
      ? null
      : measuredSetOf(
          openableForRole(fleetSet.items, opts.role),
          fleetSet.measureComplete,
        ),
    labelFor,
    openReviewHere,
    {
      slug,
      label: labelFor(slug),
      // A capped fleet list cannot be sliced for this company (the Worker
      // returns the oldest 500 across all companies), so the footer takes the
      // count the tile uses: this company's own list (integration re-check D1).
      // When that read failed there is no usable company count at all, so the
      // footer states none (integration re-check E2): a count sliced from the
      // capped list would read "0 for X" under a Try again tile.
      ...(fleetSet !== null && !fleetSet.measureComplete
        ? queueSet !== null
          ? {
              here: openableForRole(queueSet.items, opts.role).length,
              hereComplete: queueSet.measureComplete,
            }
          : { here: null }
        : {}),
    },
    monthLineShowsOpenMoneyWork(months.ok ? months.months : null),
  );

  // The balances read is itself a reading of the bank. `never_read` describes
  // the health read, so it must not turn a real balances snapshot into "we
  // could not read"; only "none" and "needs_reconnect" change the figure.
  const bankState = bankConnection(health);
  bankHost.replaceWith(
    paintBankTile(balances, bankState === "never_read" ? undefined : bankState, retry),
  );
  booksHost.replaceWith(
    paintBooksTile(
      ledgerAccounts,
      statement.ok ? statement.statement.state === "closed" : null,
      now(),
      retry,
    ),
  );
  reviewHost.replaceWith(
    paintReviewTile(
      queueSet,
      opts.role,
      slug,
      labelFor(slug),
      fleetSet === null ? 0 : openableForRole(fleetSet.items, opts.role).length,
      fleetSet?.measureComplete ?? true,
      openReviewHere,
      retry,
    ),
  );
  monthHost.replaceWith(
    paintMonthTile(statement, period, ledgerAccounts, now(), retry, opts.role),
  );
  activityHost.append(paintActivity(activity, retry));
  let flowTile = paintFlowTile({
    client,
    slug,
    role: opts.role,
    now: now(),
    period,
    ledger: ledgerAccounts,
    stillCurrent,
    onRetry: retry,
    statusReadFailed: () => opts.statusReadFailed?.() ?? false,
  });
  flowHost.replaceWith(flowTile);

  /**
   * rd r13 L3.6: repaint the two lines that speak for the health reading.
   * Check now updates the reading and the result line, but Home kept the old
   * "Connected, last checked 6 minutes ago" because it painted once. The shell
   * publishes every fleet-health read; this is the same repaint the late-read
   * path already did.
   *
   * The signature skips a repaint when the published reading says the same
   * thing: the flow tile carries Check now, and remounting it on every read
   * would restart that component for no new fact.
   */
  const readingSignature = (row: CompanyHealth | null): string =>
    `${statusReadFailed() ? "stale" : "current"}|${
      row === null
        ? "none"
        : JSON.stringify({
            bank: [
              bankConnection(row),
              bankCheckedAt(row) ?? "",
              bankNeedsReconnect(row),
            ],
            runs: row.lastRuns.map((run) => [
              run.runType,
              run.status,
              run.startedAt,
              run.completedAt ?? null,
            ]),
          })
    }`;
  const readingNow = (): CompanyHealth | null =>
    lastHealth()?.companies.find((company) => company.company === slug) ?? health;
  let paintedReading = readingSignature(health);
  const repaintFromHealth = (): void => {
    const reading = readingNow();
    const signature = readingSignature(reading);
    if (signature === paintedReading) return;
    paintedReading = signature;
    const next = paintHomeStatus(
      homeStatus(reading, ledgerAccounts, now(), {
        stale: statusReadFailed(),
        monthUnbuilt: unbuiltMonth,
        role: opts.role,
      }),
    );
    statusElement.replaceWith(next);
    statusElement = next;
    const nextFlow = paintFlowTile({
      client,
      slug,
      role: opts.role,
      now: now(),
      period,
      ledger: ledgerAccounts,
      stillCurrent,
      onRetry: retry,
      statusReadFailed: () => opts.statusReadFailed?.() ?? false,
    });
    flowTile.replaceWith(nextFlow);
    flowTile = nextFlow;
  };
  let unsubscribeHealth: (() => void) | null = null;
  unsubscribeHealth = subscribeHealth(() => {
    if (!stillCurrent()) {
      unsubscribeHealth?.();
      unsubscribeHealth = null;
      return;
    }
    repaintFromHealth();
  });

  // Round 2 (6): Home waited two seconds for the shell's status read and then
  // spoke for the previous reading. If that read settles later — and fails —
  // every line drawn from it must stop reading as current.
  if (!settledInTime) {
    void opts.healthReadSettled?.().then(() => {
      if (!stillCurrent()) return;
      repaintFromHealth();
    });
  }
}

async function loadHomeQueue(
  client: AdminClient,
  stillCurrent: () => boolean,
): Promise<MeasuredSet<QueueItem> | null> {
  try {
    const { items } = await client.listQueue({ status: "pending" });
    if (!stillCurrent()) return null;
    const pending = items.filter((item) => item.status === "pending");
    return measuredSetOf(pending, !fleetQueueListCapFired(pending.length));
  } catch {
    return null;
  }
}

/**
 * This company's pending cards. Normally a slice of the fleet list Home has
 * already read (no extra request). When the fleet list is capped it cannot be
 * sliced, so the company's own list is read instead; a failed read is null
 * (the tile offers a retry), never an empty set.
 */
async function loadCompanyQueue(
  client: AdminClient,
  slug: CompanySlug,
  fleetSet: MeasuredSet<QueueItem> | null,
  stillCurrent: () => boolean,
): Promise<MeasuredSet<QueueItem> | null> {
  if (fleetSet === null) return null;
  if (fleetSet.measureComplete) {
    return measuredSetOf(
      fleetSet.items.filter((item) => item.company === slug),
      true,
    );
  }
  try {
    const { items } = await client.listQueue({ company: slug, status: "pending" });
    if (!stillCurrent()) return null;
    const pending = items.filter(
      (item) => item.status === "pending" && item.company === slug,
    );
    return measuredSetOf(pending, !fleetQueueListCapFired(pending.length));
  } catch {
    return null;
  }
}

async function loadBalances(
  client: AdminClient,
  slug: CompanySlug,
  stillCurrent: () => boolean,
): Promise<{ ok: true; snapshot: BalancesSnapshot } | { ok: false }> {
  try {
    const snapshot = await client.getBalances(slug);
    if (!stillCurrent()) return { ok: false };
    return { ok: true, snapshot };
  } catch {
    return { ok: false };
  }
}

type LedgerAccountsLoad =
  | { ok: true; accounts: LedgerAccount[]; asOf: string }
  | { ok: false; reason: LedgerNotPublishedReason | null };

async function loadLedgerAccounts(
  client: AdminClient,
  slug: CompanySlug,
  period: string,
  stillCurrent: () => boolean,
): Promise<LedgerAccountsLoad> {
  try {
    const response = await client.getLedgerAccounts(slug, period);
    if (!stillCurrent()) return { ok: false, reason: null };
    return { ok: true, accounts: response.accounts, asOf: response.header.asOf };
  } catch (error) {
    return { ok: false, reason: reasonOf(error) };
  }
}

type StatementLoad =
  | { ok: true; statement: LedgerStatement }
  | { ok: false; reason: LedgerNotPublishedReason | null };

async function loadStatement(
  client: AdminClient,
  slug: CompanySlug,
  period: string,
  stillCurrent: () => boolean,
): Promise<StatementLoad> {
  try {
    const statement = await client.getLedgerStatement(slug, "pnl", period);
    if (!stillCurrent()) return { ok: false, reason: null };
    return { ok: true, statement };
  } catch (error) {
    return { ok: false, reason: reasonOf(error) };
  }
}

function paintBankTile(
  balances: { ok: true; snapshot: BalancesSnapshot } | { ok: false },
  bank: BankConnection | undefined,
  onRetry: () => void,
): HTMLElement {
  // The balances read is the Plaid account snapshot (ingest-worker/src/console.ts
  // getAdminBalances over account_balance_snapshots, joined to plaid_items), so
  // this figure is the bank's, never the Moov wallet's. Verified 2026-10-27.
  if (!balances.ok) return markTile(tileRetry("Bank (from your bank)", onRetry), "bank");
  // One cash fact (round-11 item 5): a bank with only a card, or a bank we
  // cannot read, is never printed as $0.00.
  const figure = cashFigure(balances.snapshot, bank);
  if (figure.kind === "no_accounts") {
    return markTile(unavailableTile("Bank (from your bank)", "No bank balance yet."), "bank");
  }
  if (figure.kind === "no_cash_account") {
    return markTile(
      unavailableTile("Bank (from your bank)", "No cash account at this bank yet."),
      "bank",
    );
  }
  if (figure.kind === "unknown") {
    return markTile(
      unavailableTile("Bank (from your bank)", "We could not read the bank balance."),
      "bank",
    );
  }
  const asOf = balances.snapshot.asOf;
  const sub = asOf !== null ? `as of ${formatAsOf(asOf)}` : undefined;
  return markTile(
    statTile("Bank (from your bank)", formatCents(figure.cents), sub),
    "bank",
  );
}

/**
 * Cash per the books. The month's closed state comes from the statement read
 * (the same month), so an open month says so here: a cash figure from a month
 * that is not closed or reconciled is provisional.
 */
function paintBooksTile(
  ledger: LedgerAccountsLoad,
  monthClosed: boolean | null,
  now: Date,
  onRetry: () => void,
): HTMLElement {
  if (!ledger.ok) {
    if (ledger.reason !== null) {
      return markTile(unavailableTile("Cash per the books", statementUnavailableCopy(ledger.reason)), "books");
    }
    return markTile(tileRetry("Cash per the books", onRetry), "books");
  }
  const cents = sumIsCashClosingCents(ledger.accounts);
  if (cents === null) return markTile(tileRetry("Cash per the books", onRetry), "books");
  const when = timeAgoText(ledger.asOf, now);
  const sub = when === "" ? "as of an unknown time" : `as of ${when}`;
  return markTile(
    statTile(
      "Cash per the books (bank and wallet)",
      formatCents(cents),
      monthClosed === false ? `${sub} · Not closed or reconciled` : sub,
    ),
    "books",
  );
}

/** The cards this person can open: an account manager never gets the owner's
 * escalated cards (the Worker hides them too, admin.ts KPB3), so a number they
 * cannot act on must never appear beside a number they can. */
function openableForRole(
  items: readonly QueueItem[],
  role: StaffRole,
): QueueItem[] {
  return reviewCards(measuredSetOf(items, true), role).openable;
}

function paintReviewTile(
  set: MeasuredSet<QueueItem> | null,
  role: StaffRole,
  company: CompanySlug,
  companyLabel: string,
  fleetOpenable: number,
  fleetComplete: boolean,
  openReview: () => void,
  onRetry: () => void,
): HTMLElement {
  if (set === null) {
    return markTile(tileRetry(navLabel("queue"), onRetry), "review");
  }
  const items = openableForRole(set.items, role).filter(
    (item) => item.company === company,
  );
  // A capped list is a lower bound, so the number says "or more" rather than
  // passing for an exact count.
  const count = countText(items.length, set.measureComplete);
  // The scope word: this tile counts ONE company, so it says which. When the
  // reader can open more elsewhere, the larger number is named here rather than
  // in a second bare number further down the page.
  const inAll = `${countOrMoreText(fleetOpenable, fleetComplete)} in all`;
  const scope =
    fleetOpenable > items.length
      ? `for ${companyLabel} · ${inAll}`
      : `for ${companyLabel}`;
  const tile = statTile(`${navLabel("queue")}: ${count}`, count, scope, () => {
    openReview();
  });
  tile.classList.add("home-review-tile");
  tile.querySelector(".stat-sub")?.setAttribute("data-role", "home-review-scope");
  return markTile(tile, "review");
}

function unavailableTile(label: string, message: string): HTMLElement {
  return h("div", { class: "stat-tile" }, [
    h("p", { class: "stat-label" }, [label]),
    h("p", { "data-role": "home-tile-message" }, [message]),
  ]);
}

/** The accounts read itself says the month could not be built. */
function ledgerUnbuilt(ledger: LedgerAccountsLoad): boolean {
  return !ledger.ok && ledger.reason === "statements_unavailable";
}

/**
 * Round 2 (3): EITHER of Home's two book reads can say the month could not be
 * built. Driving the sentence from only one of them left the month tile
 * showing "Could not be built." with nothing else when the statement read was
 * the one that failed.
 */
function monthUnbuilt(ledger: LedgerAccountsLoad, statement: StatementLoad): boolean {
  if (ledgerUnbuilt(ledger)) return true;
  return !statement.ok && statement.reason === "statements_unavailable";
}

/**
 * The month's figure is missing and the reason is the one the shared sentence
 * covers: say what happened, what to do, and give the Try again that re-reads
 * (round 2, 3).
 */
function monthUnbuiltTile(period: string, role: StaffRole, onRetry: () => void): HTMLElement {
  const retry = h(
    "button",
    {
      type: "button",
      class: "btn btn-ghost",
      "data-action": "retry-tile",
      "aria-label": "Try again: This month so far",
    },
    ["Try again"],
  );
  retry.addEventListener("click", () => {
    onRetry();
  });
  return h("div", { class: "stat-tile" }, [
    h("p", { class: "stat-label" }, ["This month so far"]),
    h("p", { "data-role": "home-tile-message" }, [booksNotBuiltSentence(period, role)]),
    retry,
  ]);
}

function markTile(tile: HTMLElement, name: string): HTMLElement {
  tile.setAttribute("data-tile", name);
  if (name === "month" || name === "flow") tile.classList.add("home-tile-wide");
  return tile;
}

function paintMonthTile(
  load: StatementLoad,
  period: string,
  ledger: LedgerAccountsLoad,
  now: Date,
  onRetry: () => void,
  role: StaffRole,
): HTMLElement {
  if (!load.ok) {
    if (load.reason !== null) {
      // Round 2 (3): the tile that carries the missing figure carries the next
      // step, whether the statement read or the accounts read is the one that
      // said the month could not be built. When the accounts read said it too,
      // the data-flowing tile already prints the sentence, so this one stays
      // short and the screen says it once.
      if (load.reason === "statements_unavailable" && !ledgerUnbuilt(ledger)) {
        return markTile(monthUnbuiltTile(period, role, onRetry), "month");
      }
      return markTile(
        unavailableTile("This month so far", statementUnavailableCopy(load.reason)),
        "month",
      );
    }
    return markTile(tileRetry("This month so far", onRetry), "month");
  }
  const pnl = asPnl(load.statement.data);
  if (pnl === null || pnl.currency !== "USD") {
    return markTile(tileRetry("This month so far", onRetry), "month");
  }
  const income = sectionTotal(pnl, "income");
  const expenses = sectionTotal(pnl, "expenses");
  const net = decimalToCents(pnl.netIncome);
  if (income === null || expenses === null || net === null) {
    return markTile(tileRetry("This month so far", onRetry), "month");
  }
  const moneyOut = expenses < 0n ? -expenses : expenses;
  const chart = twoBarChart(income, moneyOut);
  // One "Books as of" on this card: a plain phrase, never an ISO stamp or "UTC".
  const when = timeAgoText(load.statement.asOf, now);
  const asOf = when === "" ? "Books as of an unknown time" : `Books as of ${when}`;
  const band = load.statement.state === "closed"
    ? `Closed · ${asOf}`
    : `Not closed or reconciled · ${asOf}`;
  return markTile(
    h("section", { class: "stat-tile", "data-role": "stat-tile" }, [
      h("p", { class: "stat-label" }, ["This month so far"]),
      h("p", { "data-role": "home-income" }, [`Income ${formatCents(income)}`]),
      h("p", { "data-role": "home-expenses" }, [`Money out ${formatCents(moneyOut)}`]),
      h("p", { class: "stat-value", "data-role": "home-net" }, [formatCents(net)]),
      h("p", { class: "stat-sub", "data-role": "home-month-band" }, [band]),
      chart.svg,
    ]),
    "month",
  );
}

function paintFlowTile(opts: {
  client: AdminClient;
  slug: CompanySlug;
  role: StaffRole;
  now: Date;
  period: string;
  ledger: LedgerAccountsLoad;
  stillCurrent: () => boolean;
  onRetry: () => void;
  statusReadFailed: () => boolean;
}): HTMLElement {
  const health = lastHealth()?.companies.find((company) => company.company === opts.slug) ?? null;
  const bank = bankFlowCopy(health, opts.now, opts.role);
  const lines: Array<HTMLElement> = [];
  const connectionStatusLink = (): HTMLElement => {
    const link = h(
      "button",
      { type: "button", class: "btn btn-ghost", "data-action": "home-flow-connection" },
      [navLabel("health")],
    );
    link.addEventListener("click", () => {
      navigateTo("health");
    });
    return link;
  };
  if (opts.statusReadFailed() && health !== null) {
    // C-13: the last reading is still worth showing, but as the last one.
    lines.push(
      h("p", { class: "home-flow-line", "data-role": "home-flow-bank" }, [
        `We could not check just now. Last known: ${lastKnownBankText(health, opts.now)}.`,
      ]),
    );
  } else if (bank.needsReconnect) {
    // C-10: the next step is on Bank accounts, where the Reconnect action is.
    // The account manager cannot reconnect, so they are told who can.
    if (opts.role === "owner") {
      const fix = h(
        "button",
        { type: "button", class: "btn btn-ghost", "data-action": "fix-bank" },
        ["Fix it"],
      );
      fix.addEventListener("click", () => {
        navigateTo("balances");
      });
      lines.push(h("p", { class: "home-flow-line", "data-role": "home-flow-bank" }, [
        "Needs reconnecting. ",
        fix,
      ]));
    } else {
      lines.push(h("p", { class: "home-flow-line", "data-role": "home-flow-bank" }, [
        sentence("bank_needs_reconnect", opts.role),
      ]));
    }
  } else {
    lines.push(h("p", { class: "home-flow-line", "data-role": "home-flow-bank" }, [bank.text]));
  }

  const bookingText = health === null
    ? "Booking status is not available yet."
    : opts.statusReadFailed()
      // Round 2 (6): this line is drawn from the same reading as the bank line
      // above, so a failed read makes it the last known run, not the current
      // one.
      ? staleBookingCopy(health.lastRuns, opts.now)
      : bookingFlowCopy(health.lastRuns, opts.now, opts.role);
  const bookingLine = h("p", { class: "home-flow-line", "data-role": "home-flow-booking" }, [
    bookingText,
  ]);
  // C-12: the owner's failed-booking sentence names Connection status, and the
  // link to it is right here, beside Check now.
  if (
    health !== null &&
    !opts.statusReadFailed() &&
    opts.role === "owner" &&
    bookingFlowOf(health.lastRuns, opts.now).kind === "failed"
  ) {
    bookingLine.append(document.createTextNode(" "), connectionStatusLink());
  }
  lines.push(bookingLine);

  const checkHost = h("div", { "data-role": "check-now-host" });
  const booksLine = h("p", { class: "home-flow-line", "data-role": "home-flow-books" });
  if (!opts.ledger.ok && opts.ledger.reason === "ledger_not_published") {
    booksLine.append(document.createTextNode("The books view hasn't been loaded yet. "));
    const update = h(
      "button",
      { type: "button", class: "btn btn-secondary", "data-action": "update-books" },
      ["Update now"],
    );
    update.addEventListener("click", () => {
      checkHost.querySelector<HTMLButtonElement>('[data-action="check-now"]')?.click();
    });
    booksLine.append(update);
  } else if (!opts.ledger.ok && opts.ledger.reason === "statements_unavailable") {
    // C-12: the one place the unbuilt-month sentence is said, with its next
    // step and a Try again that re-reads.
    booksLine.append(
      document.createTextNode(booksNotBuiltSentence(opts.period, opts.role)),
    );
    const retry = h(
      "button",
      { type: "button", class: "btn btn-ghost", "data-action": "retry-tile", "aria-label": "Try again: Books view" },
      ["Try again"],
    );
    retry.addEventListener("click", () => {
      opts.onRetry();
    });
    booksLine.append(document.createTextNode(" "), retry);
    if (opts.role === "owner") {
      booksLine.append(document.createTextNode(" "), connectionStatusLink());
    }
  } else if (!opts.ledger.ok && opts.ledger.reason !== null) {
    booksLine.append(document.createTextNode(statementUnavailableCopy(opts.ledger.reason)));
  } else if (!opts.ledger.ok) {
    booksLine.append(document.createTextNode("Books view could not be loaded. "));
    const retry = h(
      "button",
      { type: "button", class: "btn btn-ghost", "data-action": "retry-tile", "aria-label": "Try again: Books view" },
      ["Try again"],
    );
    retry.addEventListener("click", () => {
      opts.onRetry();
    });
    booksLine.append(retry);
  } else {
    const stale = isOlderThanDay(opts.ledger.asOf, opts.now);
    if (stale) booksLine.classList.add("home-flow-stale");
    booksLine.setAttribute("data-stale", stale ? "true" : "false");
    const when = timeAgoText(opts.ledger.asOf, opts.now);
    booksLine.append(
      document.createTextNode(
        `Books view last loaded ${when === "" ? "an unknown time ago" : when}`,
      ),
    );
  }
  lines.push(booksLine);

  const tile = h("section", { class: "stat-tile", "data-role": "stat-tile", "data-tile": "flow" }, [
    h("p", { class: "stat-label" }, ["Is my data flowing?"]),
    ...lines,
    checkHost,
    h("p", { class: "home-flow-note", "data-role": "home-flow-timing" }, [BOOKS_REFRESH_COPY]),
  ]);
  tile.classList.add("home-tile-wide");
  renderCheckNow({
    client: opts.client,
    // The check-now module takes the wire key, not words.
    company: opts.slug.valueOf(),
    root: checkHost,
    stillCurrent: opts.stillCurrent,
  });
  return tile;
}

export async function renderHomePage(opts: HomePageOptions): Promise<void> {
  if (opts.scope.kind === "company") {
    await renderCompanyHome(opts);
    return;
  }
  await renderFleetHome(opts);
}

async function renderFleetHome(opts: HomePageOptions): Promise<void> {
  const { client, root } = opts;
  const stillCurrent = (): boolean => opts.isCurrent?.() ?? true;
  clear(root);

  const loadedAt = new Date();
  const freshnessHost = h("div", { "data-role": "freshness-host" });
  root.append(freshnessHost);
  freshnessHost.append(
    createFreshnessCaption({
      updatedAt: loadedAt,
      onRefresh: () => {
        // Refresh drops a notice left by an action; it does not repeat it
        // (round 2, MUST-FIX 1).
        void renderHomePage({ ...opts, needsYouNotice: undefined });
      },
    }),
  );

  const headline = h("h1", {
    class: "home-headline",
    "data-role": "home-headline",
  }, ["Loading…"]);
  root.append(headline);

  const actionNotice = needsYouNoticeLine(opts);
  if (actionNotice !== null) root.append(actionNotice);

  const asOfLine = h("p", {
    class: "home-as-of muted",
    "data-role": "home-as-of",
    hidden: "true",
  });
  root.append(asOfLine);

  const rowsHost = h("div", {
    class: "home-rows",
    role: "list",
    "data-role": "home-rows",
  });
  root.append(rowsHost);
  rowsHost.append(h("p", { class: "muted", "data-role": "home-loading" }, ["Loading…"]));

  const needsYou = h("section", {
    class: "needs-you",
    "data-role": "needs-you",
    hidden: "true",
  });
  root.append(needsYou);

  const todayOnboarding = h("section", {
    class: "today-onboarding",
    "data-role": "today-onboarding",
    hidden: "true",
  });
  root.append(todayOnboarding);

  const awareness = h("p", {
    class: "home-awareness",
    "data-role": "home-awareness",
  });
  root.append(awareness);

  const members = resolveScopeMembers(opts.scope, opts.groups, opts.companySlugs);
  const directory = directoryFor(opts);
  const labelFor = (slug: CompanySlug): string =>
    face.company(slug, directory, "sentence");

  // Onboarding is its own independent read. Start it, but let the queue and
  // awareness paint first: a slow (or unavailable) status fan-out must not
  // hold the owner's Home. The section paints itself when it resolves and
  // drops its result if this render was superseded.
  void paintTodayOnboarding(
    todayOnboarding,
    members,
    opts.role,
    labelFor,
    client,
    opts.openChecklist,
    stillCurrent,
  ).catch(() => undefined);

  // Queue first (awareness + row counts) — balances fan-out is lazy garnish.
  // Completeness is part of the value: a capped page is not a complete set.
  let queueSet: MeasuredSet<QueueItem> | null;
  try {
    const { items } = await client.listQueue({ status: "pending" });
    if (!stillCurrent()) return;
    const pending = items.filter((i) => i.status === "pending");
    queueSet = measuredSetOf(pending, !fleetQueueListCapFired(pending.length));
  } catch {
    if (!stillCurrent()) return;
    queueSet = null;
  }

  // Completeness (C-20): a failed queue read must not paint "0 pending".
  const countsByCompany =
    queueSet === null ? null : countPendingByCompany(queueSet.items);
  // Pass the MeasuredSet — do not strip measureComplete at the seam (R21C-2).
  paintNeedsYou(needsYou, queueSet, labelFor, client, opts.role, async (notice) => {
    // The notice belongs to this one re-read: the next render starts clean
    // (round 2, MUST-FIX 1).
    await renderHomePage({ ...opts, needsYouNotice: notice });
  }, opts.openReview, stillCurrent);
  paintAwareness(awareness, queueSet, labelFor, opts.openReview, undefined, false);

  // Headline placeholder until balances complete — never a silent partial sum.
  clear(headline);
  headline.append(document.createTextNode(scopeLabel(opts.scope, opts.groups)));

  if (members.length === 0) {
    clear(rowsHost);
    rowsHost.append(
      h("p", { class: "empty", "data-role": "home-empty" }, [
        "No companies in this scope.",
      ]),
    );
    return;
  }

  // Fan-out on visit / Refresh only (condition 7). Bounded concurrency; no retries.
  const results = await mapWithConcurrency(members, HOME_BALANCES_CONCURRENCY, async (slug) => {
    try {
      const snapshot = await client.getBalances(slug);
      const figure = cashFigure(snapshot);
      // A company with no accounts is empty; a company whose accounts hold no
      // cash is NOT a usable $0 in the group total (round-11 item 5).
      if (figure.kind === "no_accounts" && snapshot.asOf === null) {
        return { slug, result: { status: "empty" as const, snapshot } };
      }
      const cashCents = figure.kind === "cash" ? figure.cents : null;
      return { slug, result: { status: "ok" as const, snapshot, cashCents } };
    } catch {
      return { slug, result: { status: "error" as const } };
    }
  });
  if (!stillCurrent()) return;

  const bySlug = new Map(results.map((r) => [r.slug, r.result]));
  // Completeness (C-20): failed OR unusable cash (null) blocks the group total.
  const anyIncomplete = results.some((r) => {
    const res = r.result;
    if (res.status === "error") return true;
    if (res.status === "ok" && res.cashCents === null) return true;
    return false;
  });

  // Headline: omit total until ALL members report usable cash (§3.3 / C-20).
  clear(headline);
  if (anyIncomplete) {
    headline.append(document.createTextNode(scopeLabel(opts.scope, opts.groups)));
  } else {
    const cashLine = formatGroupCashHeadline(results.map((r) => r.result), opts.scope, opts.groups);
    headline.append(document.createTextNode(cashLine));
  }

  // asOf caption: oldest across successful members; omit on null (§4.4 honesty).
  const asOfs = results
    .map((r) => r.result)
    .filter((r): r is Extract<MemberBalance, { status: "ok" | "empty" }> =>
      r.status === "ok" || r.status === "empty",
    )
    .map((r) => r.snapshot.asOf)
    .filter((d): d is string => d !== null);
  clear(asOfLine);
  if (!anyIncomplete && asOfs.length > 0) {
    const oldest = asOfs.reduce((a, b) => (a < b ? a : b));
    asOfLine.hidden = false;
    asOfLine.removeAttribute("hidden");
    asOfLine.append(document.createTextNode(`Balances as of ${formatAsOf(oldest)}.`));
  } else {
    asOfLine.hidden = true;
    asOfLine.setAttribute("hidden", "true");
  }

  const queueMeasureComplete = queueSet?.measureComplete ?? false;
  clear(rowsHost);
  for (const slug of members) {
    const result = bySlug.get(slug) ?? { status: "error" as const };
    const pending = countsByCompany === null ? null : (countsByCompany.get(slug) ?? 0);
    rowsHost.append(
      renderCompanyRow({
        slug,
        label: labelFor(slug),
        result,
        pendingCount: pending,
        queueMeasureComplete: countsByCompany === null ? false : queueMeasureComplete,
        onOpen: () => {
          opts.openAccounts(slug);
        },
        onRetry: () => {
          // A row retry re-reads the page; it drops a stale notice.
          void renderHomePage({ ...opts, needsYouNotice: undefined });
        },
      }),
    );
  }
}

/**
 * Today's onboarding section. One status GET per company in scope per page
 * load (the brief's bound); a 404 means no open run and adds nothing. Only
 * steps in `waiting` for the signed-in person count — a blocked step is not
 * that person's turn yet.
 */
export async function paintTodayOnboarding(
  host: HTMLElement,
  members: readonly string[],
  role: StaffRole,
  labelFor: (slug: CompanySlug) => string,
  client: AdminClient,
  openChecklist: (slug: CompanySlug) => void,
  stillCurrent: () => boolean,
): Promise<void> {
  clear(host);
  host.hidden = true;
  host.setAttribute("hidden", "true");
  if (members.length === 0) return;

  const results = await mapWithConcurrency(
    members,
    HOME_BALANCES_CONCURRENCY,
    async (
      slug,
    ): Promise<{ slug: CompanySlug; status: OnboardingStatus | null; failed: boolean }> => {
      try {
        return { slug, status: await client.getOnboardingStatus(slug), failed: false };
      } catch (error) {
        // No open run (404), no access, or an unavailable read: add nothing.
        // Never claim "nothing waiting" from a failed read. A 404 or 403 is an
        // answer (no open run, or nothing for this person there); anything else
        // is a company we could not count, so the totals below are a lower bound.
        const noRun =
          error instanceof AdminApiError && (error.status === 404 || error.status === 403);
        return { slug, status: null, failed: !noRun };
      }
    },
  );
  if (!stillCurrent()) return;

  const waiting: { slug: CompanySlug; count: number }[] = [];
  for (const row of results) {
    if (row.status === null) continue;
    // One checklist reading (B2a-4): the same count the checklist page shows.
    const count = actionableFor(row.status, role);
    if (count > 0) waiting.push({ slug: row.slug, count });
  }
  if (waiting.length === 0) return;

  const total = waiting.reduce((sum, row) => sum + row.count, 0);
  const everyCompanyRead = results.every((row) => !row.failed);
  host.hidden = false;
  host.removeAttribute("hidden");
  host.append(
    h("h2", { class: "needs-you-heading", "data-role": "today-onboarding-heading" }, [
      "Onboarding waiting on you",
    ]),
    h("p", { class: "muted", "data-role": "today-onboarding-count" }, [
      `${countedText(waiting.length, everyCompanyRead, "company", "companies")} · ${countedText(total, everyCompanyRead, "step", "steps")} waiting on you.`,
    ]),
  );
  const list = h("ul", {
    class: "today-onboarding-list",
    "data-role": "today-onboarding-list",
  });
  for (const row of waiting) {
    const rowLabel = labelFor(row.slug);
    const link = h(
      "button",
      {
        type: "button",
        class: "btn btn-ghost",
        "data-role": "today-onboarding-link",
        "data-company": row.slug,
        "data-step-count": String(row.count),
        "aria-label": `Open the onboarding checklist for ${labelFor(row.slug)}`,
      },
      [
        `${rowLabel} · ${String(row.count)} ${
          row.count === 1 ? "step" : "steps"
        } waiting`,
      ],
    );
    link.addEventListener("click", () => {
      openChecklist(row.slug);
    });
    list.append(h("li", {}, [link]));
  }
  host.append(list);
}

function renderCompanyRow(opts: {
  slug: CompanySlug;
  label: string;
  result: MemberBalance;
  pendingCount: number | null;
  /** False when the fleet queue page is incomplete (failed read or list cap). */
  queueMeasureComplete: boolean;
  onOpen: () => void;
  onRetry: () => void;
}): HTMLElement {
  const { slug, label, result, pendingCount, queueMeasureComplete } = opts;
  const pendingStatus = pendingStatusAttr(pendingCount, queueMeasureComplete);

  if (result.status === "error") {
    const retry = h(
      "button",
      {
        type: "button",
        class: "btn btn-ghost",
        "data-action": "retry-member",
        "data-company": slug,
        "aria-label": `Retry balances for ${label}`,
      },
      ["Retry"],
    );
    retry.addEventListener("click", (event) => {
      event.stopPropagation();
      opts.onRetry();
    });
    const row = h(
      "div",
      {
        class: "home-row home-row-error",
        role: "listitem",
        "data-role": "home-row",
        "data-company": slug,
        "data-status": "error",
      },
      [
        h("div", { class: "home-row-main" }, [
          h("span", { class: "home-row-company" }, [label]),
          h("span", { class: "home-row-cash muted" }, [
            `Couldn't check ${label} — `,
            retry,
          ]),
        ]),
        h("span", {
          class: "home-row-pending muted",
          "data-role": "home-row-pending",
          "data-pending-status": pendingStatus,
        }, [
          pendingLabel(pendingCount, queueMeasureComplete),
        ]),
      ],
    );
    return row;
  }

  // ok with cashCents null (mixed currency / malformed) is incomplete — show
  // em dash, never a fabricated partial figure (C-20).
  const cashText =
    result.status === "ok" && result.cashCents !== null
      ? formatCents(result.cashCents)
      : "—";
  const asOf =
    result.snapshot.asOf !== null ? `as of ${formatAsOf(result.snapshot.asOf)}` : "";

  const row = h(
    "button",
    {
      type: "button",
      class: "home-row",
      role: "listitem",
      "data-role": "home-row",
      "data-company": slug,
      "data-status": result.status === "ok" && result.cashCents === null ? "incomplete" : "ok",
      "aria-label": `Open ${navLabel("balances")} for ${label}`,
    },
    [
      h("div", { class: "home-row-main" }, [
        h("span", { class: "home-row-company" }, [label]),
        h("span", { class: "home-row-cash", "data-role": "home-row-cash" }, [cashText]),
        asOf
          ? h("span", { class: "home-row-asof muted" }, [asOf])
          : null,
      ]),
      h("span", {
        class: "home-row-pending",
        "data-role": "home-row-pending",
        "data-pending-status": pendingStatus,
      }, [pendingLabel(pendingCount, queueMeasureComplete)]),
    ],
  );
  row.addEventListener("click", () => {
    opts.onOpen();
  });
  return row;
}

function pendingStatusAttr(
  count: number | null,
  measureComplete: boolean,
): "ok" | "error" | "incomplete" {
  if (count === null) return "error";
  if (!measureComplete) return "incomplete";
  return "ok";
}

/**
 * Pending count label.
 * - null → fleet queue read failed
 * - measureComplete false → capped/truncated page; 0 is unknown, N is a lower bound
 * - measureComplete true → exact count (including quiet "0 pending")
 */
export function pendingLabel(
  count: number | null,
  measureComplete = true,
): string {
  if (count === null) return "Couldn't check";
  if (!measureComplete) {
    if (count <= 0) return "Pending unknown";
    return count === 1 ? "≥1 pending" : `≥${String(count)} pending`;
  }
  if (count <= 0) return "0 pending";
  return count === 1 ? "1 pending" : `${String(count)} pending`;
}

function countPendingByCompany(items: readonly QueueItem[]): Map<string, number> {
  const map = new Map<string, number>();
  for (const item of items) {
    map.set(item.company, (map.get(item.company) ?? 0) + 1);
  }
  return map;
}

/**
 * The company the footer speaks for. `here` is that company's own count when
 * the fleet list is capped (the slice of a capped list is meaningless):
 * a number, or null when the company's own read failed. `hereComplete` false
 * means that list is capped too, so the count is a lower bound.
 */
export interface AwarenessCompany {
  slug: CompanySlug;
  label: string;
  here?: number | null;
  hereComplete?: boolean;
}

/**
 * Awareness line — fleet-wide review_queue only; Review → Today stage.
 * Takes a MeasuredSet so a capped page cannot claim a complete fleet map.
 *
 * `singleCompany` is the company this page is showing. `set` must be the whole
 * fleet's pending list (the nav badge's list), never one company's slice: when
 * every waiting question is that company's the line says so in words, and
 * otherwise it names both counts with their scope ("19 for X · 34 in all").
 */
export function formatAwarenessLine(
  set: MeasuredSet<QueueItem>,
  labelFor: (slug: CompanySlug) => string,
  singleCompany?: AwarenessCompany,
  monthUnclear = false,
): string {
  const items = set.items;
  const n = items.length;
  if (n === 0) {
    // Mirror: genuine empty stays quiet — only via the helper when complete.
    // Never `?? "All caught up."` (queue.ts R20C-6): null → refuse clear claim.
    // B2a-4: the claim also refuses while Home's month line shows open
    // statements or bank lines; the cards being clear is not the month.
    const claim = measuredClearHeadline({
      workCount: monthUnclear ? 1 : 0,
      measureComplete: set.measureComplete,
      clearCopy: "All caught up.",
      incompleteCopy: "Couldn't finish checking pending questions.",
    });
    if (claim === null) {
      return "Couldn't finish checking pending questions.";
    }
    return claim;
  }
  const noun = n === 1 ? "question" : "questions";
  const capped = set.measureComplete ? "" : " (list capped — more may exist)";
  // Every count below goes through the one helper: a capped list says "or more".
  const howMany = (count: number, complete: boolean): string => countOrMoreText(count, complete);
  if (singleCompany !== undefined) {
    // "Every question..." is a claim about the fleet, so it needs the whole
    // fleet set: callers must pass the fleet list, never one company's slice.
    // When that list is capped this company's own count is passed in, because
    // the slice of a capped list can be empty for a company that has cards.
    // With no usable company count (the read failed, or none was passed) the
    // line states none at all: it never slices the capped list (re-check E2).
    if (!set.measureComplete) {
      const own = singleCompany.here;
      if (typeof own !== "number") {
        return `${howMany(n, set.measureComplete)} ${noun} waiting in all${capped}`;
      }
      const ownText = howMany(own, singleCompany.hereComplete !== false);
      return `${ownText} for ${singleCompany.label} · ${howMany(n, set.measureComplete)} in all${capped}`;
    }
    const here =
      typeof singleCompany.here === "number"
        ? singleCompany.here
        : items.filter((item) => item.company === singleCompany.slug).length;
    if (here === n) {
      return `Every question waiting is for ${singleCompany.label}.`;
    }
    return `${howMany(here, set.measureComplete)} for ${singleCompany.label} · ${howMany(n, set.measureComplete)} in all`;
  }
  // A capped fleet list holds only the oldest cards, so no company's share of
  // it is known: slicing it by company would print "500 for Trust" beside rows
  // that read "Trust ≥500 pending" and "Rentals Pending unknown" (F4). The line
  // states the one thing the list does know, as a lower bound.
  if (!set.measureComplete) {
    return `${howMany(n, set.measureComplete)} ${noun} waiting across all companies${capped}`;
  }
  const byCompany = countPendingByCompany(items);
  const companies = [...byCompany.keys()];
  let base: string;
  if (companies.length > 3) {
    base = `${howMany(n, set.measureComplete)} ${noun} waiting across ${String(companies.length)} companies`;
  } else {
    const parts = companies.map(
      (slug) => `${String(byCompany.get(slug) ?? 0)} for ${labelFor(slug)}`,
    );
    base = `${howMany(n, set.measureComplete)} ${noun} waiting — ${parts.join(", ")}`;
  }
  return base;
}

function paintNeedsYou(
  host: HTMLElement,
  set: MeasuredSet<QueueItem> | null,
  labelFor: (slug: CompanySlug) => string,
  client: AdminClient,
  role: StaffRole,
  refresh: (notice?: string) => void | Promise<void>,
  openReview: () => void,
  stillCurrent: () => boolean,
): void {
  clear(host);
  if (set === null) {
    host.hidden = true;
    host.setAttribute("hidden", "true");
    return;
  }
  // Escalations visible on this page of the measure. Incomplete measure does
  // not invent a "needs you" empty claim — awareness owns the clear/incomplete
  // fleet sentence. Hiding with no escalated rows is silence, not "all clear."
  // The one card fact: the owner holds these; an account manager may see them
  // as "Needs you" (owner decision A, 2026-10-28) but never opens them here.
  const escalated = reviewCards(set, "owner").withOwner;
  if (escalated.length === 0) {
    host.hidden = true;
    host.setAttribute("hidden", "true");
    return;
  }
  host.hidden = false;
  host.removeAttribute("hidden");

  host.append(
    h("div", {
      class: "aria-live",
      "aria-live": "polite",
      "data-role": "needs-you-live",
    }),
  );

  // Round 11 (R7-16): a failure is visible, not only announced to a screen
  // reader, and it is a status so assistive technology reads it too.
  const failureLine = h("p", {
    class: "status banner-err",
    role: "status",
    "data-role": "needs-you-error",
    hidden: "true",
  });
  host.append(failureLine);

  host.append(
    h("h2", { class: "needs-you-heading", "data-role": "needs-you-heading" }, [
      "Needs you",
    ]),
  );

  const list = h("ul", {
    class: "needs-you-list",
    "data-role": "needs-you-list",
  });
  for (const item of escalated) {
    const escalatedFor = labelFor(item.company);
    // 6(c): the raw actor id ("demo-am") is an internal key, not a person's
    // name. A value that reads as a name is shown; anything else is "an
    // account manager", which is who escalates these cards.
    const escalatedByName = escalatedByText(
      amVisibleDisplayText(item.escalatedBy ?? "").trim(),
    );
    const returnBtn = h(
      "button",
      {
        type: "button",
        class: "btn btn-ghost",
        "data-action": "return-to-am",
        "data-queue-id": String(item.id),
        "aria-label": `Return ${labelFor(item.company)} item to account manager`,
      },
      ["Return to account manager"],
    );
    returnBtn.addEventListener("click", () => {
      void (async () => {
        // Round 3, SHOULD-FIX 3: the notice an earlier action left is about
        // that action. When this Return starts, it goes, so an old 409
        // sentence cannot sit beside this action's new error.
        host.parentElement?.querySelector('[data-role="needs-you-notice"]')?.remove();
        clear(failureLine);
        failureLine.hidden = true;
        failureLine.setAttribute("hidden", "true");
        // The one write primitive (B2a-4): the re-read is `afterLanded`, so a
        // failed re-read never says "did not save".
        const outcome = await runWrite({
          key: `return-to-am:${String(item.id)}`,
          scope: host,
          gate: () => null,
          send: () => client.returnQueueItemToAm(item.company, item.id),
          afterLanded: () => refresh(),
          stillCurrent,
        });
        if (!stillCurrent()) return;
        if (outcome.kind === "landed") return;
        if (isAlreadyResolvedConflict(outcome.error)) {
          // Only the Worker's already-resolved 409 is "someone answered this
          // card"; Home re-reads its card list, which removes the row (B1a's
          // rule). Every other 409 (the card changed, it is not escalated) is
          // a conflict the person can retry, and falls through to the one
          // write-outcome wording below (round 2, B2a LOW).
          const copy = failureCopy(outcome.error, { role, context: "card" });
          failureLine.textContent = copy.sentence;
          failureLine.hidden = false;
          failureLine.removeAttribute("hidden");
          await refresh(copy.sentence);
          return;
        }
        if (outcome.kind === "refused" && outcome.status === 0) {
          // The gate announced its own reason; nothing was sent.
          return;
        }
        failureLine.hidden = false;
        failureLine.removeAttribute("hidden");
        clear(failureLine);
        if (outcome.error !== undefined) {
          const copy = failureCopy(outcome.error, { role, context: "write" });
          failureLine.append(document.createTextNode(copy.sentence));
          if (copy.detail !== null) {
            failureLine.append(
              disclosure("Show details", h("p", {}, [copy.detail])),
            );
          }
        } else {
          failureLine.append(
            document.createTextNode(outcomeSentence(outcome, role)),
          );
        }
        // A real failure keeps the button usable.
        returnBtn.disabled = false;
      })();
    });
    const reviewBtn = h(
      "button",
      {
        type: "button",
        class: "btn btn-ghost",
        "data-action": "needs-you-review",
        "aria-label": `Review in ${navLabel("queue")}`,
      },
      ["Review"],
    );
    reviewBtn.addEventListener("click", () => {
      openReview();
    });
    list.append(
      h("li", {
        class: "needs-you-item",
        "data-role": "needs-you-item",
        "data-queue-id": String(item.id),
      }, [
        h("p", { class: "needs-you-meta" }, [
          `${escalatedFor} · escalated by ${escalatedByName}`,
        ]),
        h("p", { class: "needs-you-note", "data-role": "needs-you-note" }, [
          amVisibleDisplayText(item.escalationNote ?? ""),
        ]),
        h("div", { class: "needs-you-actions" }, [reviewBtn, returnBtn]),
      ]),
    );
  }
  host.append(list);
}

function paintAwareness(
  host: HTMLElement,
  set: MeasuredSet<QueueItem> | null,
  labelFor: (slug: CompanySlug) => string,
  openReview: () => void,
  singleCompany?: AwarenessCompany,
  monthUnclear = false,
): void {
  clear(host);
  if (set === null) {
    host.append(document.createTextNode("Couldn't check pending questions."));
    return;
  }
  const line = formatAwarenessLine(set, labelFor, singleCompany, monthUnclear);
  if (set.items.length === 0) {
    host.append(document.createTextNode(line));
    return;
  }
  const review = h(
    "button",
    {
      type: "button",
      class: "btn btn-ghost",
      "data-action": "home-review",
      "aria-label": "Review pending questions",
    },
    ["Review"],
  );
  review.addEventListener("click", () => {
    openReview();
  });
  // No separator before the button: at phone width the line wraps and a "·"
  // left at the end of the first row dangles in front of "Review".
  host.append(document.createTextNode(`${line} `), review);
  if (!set.measureComplete) {
    host.setAttribute("data-queue-cap", "fired");
  } else {
    host.removeAttribute("data-queue-cap");
  }
}

/**
 * Group cash headline. Callers must only invoke this when every member has
 * reported usable cash (no errors, no null cashCents). Partial sums are never
 * entitled to render (C-20 / console-personas §3.3).
 */
export function formatGroupCashHeadline(
  results: MemberBalance[],
  scope: HomeScope,
  groups: readonly DashboardGroup[],
): string {
  const label = scopeLabel(scope, groups);
  const cashValues: UsdCents[] = [];
  for (const r of results) {
    if (r.status === "error") return label;
    if (r.status === "ok") {
      if (r.cashCents === null) return label;
      cashValues.push(r.cashCents);
    }
    // empty members contribute nothing (no accounts yet) — skip.
  }
  const total = sumUsdCentsExact(cashValues);
  if (total === null) return label;
  return `${formatCents(total)} across ${label}`;
}

/** Exported for tests — RouteName of the Review destination. */
export const HOME_REVIEW_ROUTE: RouteName = "queue";
